import { JSX } from 'react';
import { toast, ToastContent, ToastOptions, TypeOptions, Id } from 'react-toastify';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';

type IconName = (typeof ICONS)[keyof typeof ICONS];

const TOAST_ICON_MAP: Record<string, IconName> = {
  info: ICONS.TOAST_INFO,
  default: ICONS.TOAST_INFO,
  warning: ICONS.TOAST_WARNING,
  success: ICONS.TOAST_SUCCESS,
  error: ICONS.TOAST_ERROR,
};

const getToastIcon = (type: TypeOptions = 'default'): JSX.Element => {
  const iconName: IconName = TOAST_ICON_MAP[type] ?? ICONS.TOAST_INFO;
  return <Icon color="var(--global-ico-text-1)" name={iconName} size={24} />;
};

interface ToastTriggerConfig extends ToastOptions {
  description?: string;
  content?: ToastContent;
  stacked?: boolean;
}

/**
 * A wrapper for react-toastify that standardizes the UI for
 * Title/Description layouts and consistent iconography.
 * * @param message - The primary heading text for the toast.
 * @param config - Optional configuration including description, custom content, and toast options.
 */
const toastTrigger = (message: string, config: ToastTriggerConfig = {}): Id => {
  const { description, content, type = 'default', icon, className = '', ...rest } = config;

  // Prioritize custom 'content' prop, otherwise render the standard Message/Description block
  const renderedContent: ToastContent = content ?? (
    <div style={{ paddingRight: '18px' }}>
      <Text className="H6" design="GlobalIcoText1">
        {message}
      </Text>
      {description && (
        <Text className="P2" design="GlobalIcoText3">
          {description}
        </Text>
      )}
    </div>
  );

  return toast(renderedContent, {
    type,
    className: `${className} ${type}-toast`.trim(),
    // Fallback logic: provided icon -> type-based icon -> default
    icon: icon ?? getToastIcon(type),
    ...rest,
  });
};

export default toastTrigger;
